<template>
  <div class="shop-articles">
    <div v-if="loading" class="articles-loader">
      <v-progress-circular indeterminate color="#00BCD4" size="32" />
    </div>

    <template v-else-if="displayedItems.length">
      <!-- GRID -->
      <div v-if="viewMode === 'grid'" class="art-grid">
        <div
          v-for="item in displayedItems"
          :key="item.id"
          class="art-card"
          @click="$emit('add-item', item)"
        >
          <!-- Badge s količinom u košarici -->
          <div v-if="cartQuantities[item.id]" class="art-qty-badge">
            {{ cartQuantities[item.id] }}
          </div>

          <!-- Slika — samo ako postoji; bez ikakve placeholder zone -->
          <div v-if="item.picture && !errorImageIds.has(item.id)" class="art-img-wrap">
            <img
              :src="item.picture"
              loading="lazy"
              class="art-img-el"
              @error="onImageError(item.id)"
            />
          </div>

          <div class="art-body">
            <div class="art-name" :title="item.name" :style="{ fontSize: fontSize + 'px' }">{{ item.name }}</div>
            <div class="art-price" :style="{ fontSize: (fontSize + 1) + 'px' }">{{ displayPrice(item) }}</div>
          </div>
        </div>
      </div>

      <!-- LIST -->
      <div v-else class="art-list">
        <div
          v-for="item in displayedItems"
          :key="item.id"
          class="art-list-row"
          @click="$emit('add-item', item)"
        >
          <span class="art-list-name" :style="{ fontSize: fontSize + 'px' }">{{ item.name }}</span>
          <span v-if="item.code" class="art-list-code">{{ item.code }}</span>
          <v-chip v-if="cartQuantities[item.id]" x-small color="rgba(0,188,212,0.12)" text-color="#00BCD4" class="mr-1">
            {{ cartQuantities[item.id] }}×
          </v-chip>
          <span class="art-list-price" :style="{ fontSize: fontSize + 'px' }">{{ displayPrice(item) }}</span>
        </div>
      </div>
    </template>

    <div v-else class="articles-empty">
      <v-icon size="40" color="#E0E6ED">mdi-package-variant-closed</v-icon>
      <span>{{ searchQuery ? 'Nema rezultata' : 'Nema artikala u kategoriji' }}</span>
    </div>
  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'

const CACHE_TTL = 5 * 60 * 1000 // 5 minuta

export default {
  name: 'ShopArticles',

  props: {
    categoryId: { type: String, default: '' },
    searchQuery: { type: String, default: '' },
    viewMode: { type: String, default: 'grid' },
    cartQuantities: { type: Object, default: () => ({}) },
    wholesaleView: { type: Boolean, default: false },
    fontSize: { type: Number, default: 15 }
  },

  data () {
    return {
      loading: false,
      // { [categoryId]: { items: [], loadedAt: number } }
      cache: {},
      errorImageIds: new Set()
    }
  },

  computed: {
    currency () {
      return state.getCurrentCompany()?.currency || 'EUR'
    },
    cachedItems () {
      const entry = this.cache[this.categoryId]
      if (!entry) return null
      if (Date.now() - entry.loadedAt > CACHE_TTL) return null
      return entry.items
    },
    displayedItems () {
      const items = this.cachedItems || []
      if (!this.searchQuery) return items
      const q = this.searchQuery.toLowerCase()
      return items.filter(i =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.code && i.code.toLowerCase().includes(q))
      )
    }
  },

  watch: {
    categoryId: {
      immediate: true,
      handler (id) {
        if (id) this.loadCategory(id)
      }
    }
  },

  methods: {
    formatPrice (priceInCents) {
      if (!priceInCents && priceInCents !== 0) return ''
      return (priceInCents / 100).toFixed(2).replace('.', ',') + ' €'
    },
    displayPrice (item) {
      const mpc = item.price || 0
      if (!this.wholesaleView) return this.formatPrice(mpc)
      // VPC = MPC / (1 + taxRate)
      const taxes = item.prices?.[this.currency]?.taxes
      const rateBp = (taxes && taxes.length) ? taxes[0].rate : 2500
      const vpc = Math.round(mpc / (1 + rateBp / 10000))
      return this.formatPrice(vpc)
    },

    onImageError (itemId) {
      this.errorImageIds.add(itemId)
      this.$forceUpdate()
    },

    async loadCategory (categoryId) {
      const entry = this.cache[categoryId]
      // Cache hit — valid and not expired
      if (entry && Date.now() - entry.loadedAt < CACHE_TTL) return
      // Expired — evict so next access fetches fresh data
      if (entry) this.$delete(this.cache, categoryId)

      this.loading = true
      try {
        const query = await df
          .collection(`location_pricelist/${state.getPointOfSale().id}/items`)
          .where('category_ids', 'array-contains', categoryId)
          .where('status', '==', 'OK')
          .where('active', '==', true)
          .get()

        const items = []
        query.docs.forEach(doc => {
          const p = doc.data()
          if (p.warehouse_units && p.warehouse_units.units) {
            Object.keys(p.warehouse_units.units).forEach(key => {
              const u = p.warehouse_units.units[key]
              if (u.prices && u.prices[this.currency] && u.prices[this.currency].active) {
                items.push({
                  id: `${p.id}.${key}`,
                  name: u.name || p.name,
                  price: u.prices[this.currency].price || 0,
                  prices: u.prices,
                  selling_unit: u.storage_unit || key,
                  unit: u.storage_unit || key,
                  code: p.code || '',
                  picture: p.picture || null,
                  type: 'product',
                  kpdcode: p.kpdcode || '',
                  naknade: u.naknade || p.naknade || undefined,
                  manual_price_input: p.manual_price_input || false
                })
              }
            })
          } else {
            const price = p.prices?.[this.currency]?.price || 0
            items.push({
              id: p.id,
              name: p.name,
              price,
              prices: p.prices || {},
              selling_unit: p.selling_unit || 'kom',
              unit: p.selling_unit || 'kom',
              code: p.code || '',
              picture: p.picture || null,
              type: 'product',
              kpdcode: p.kpdcode || '',
              naknade: p.naknade || undefined,
              manual_price_input: p.manual_price_input || false
            })
          }
        })

        items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'hr'))
        this.$set(this.cache, categoryId, { items, loadedAt: Date.now() })
      } catch (err) {
        console.error('ShopArticles: greška pri dohvatu artikala', err)
      } finally {
        this.loading = false
      }
    }
  },

  beforeDestroy () {
    this.cache = {}
  }
}
</script>

<style scoped>
.shop-articles {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #EEF2F6;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,188,212,0.5) transparent;
}
.articles-loader {
  display: flex;
  justify-content: center;
  padding-top: 40px;
}
.articles-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 48px;
  font-size: 12px;
  color: #B0BEC5;
}
/* GRID */
.art-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}
.art-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: visible; /* badge lebdi izvan obruba */
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transition: box-shadow 0.15s, transform 0.12s;
  user-select: none;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 96px;
}
.art-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.10);
  transform: translateY(-2px);
}
.art-img-wrap {
  height: 56px;    /* ~gornja polovica od 108px */
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
}
.art-img-el { width: 100%; height: 100%; object-fit: cover; }

/* Badge — gornji desni kut, "lebdi" izvan kartice */
.art-qty-badge {
  position: absolute;
  top: -6px; right: -6px;
  background: #00BCD4;
  color: white;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0,188,212,0.35);
  border: 2px solid #F8FAFC;
}

/* Tijelo kartice: naziv gore, cijena dolje */
.art-body {
  flex: 1;
  padding: 10px 12px 9px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
}
.art-name {
  font-size: 15px;
  color: #374151;
  font-weight: 600;
  line-height: 1.45;
  text-transform: capitalize;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.art-price {
  font-size: 16px;
  color: #00BCD4;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: -0.3px;
}
.shop-articles::-webkit-scrollbar { width: 5px; }
.shop-articles::-webkit-scrollbar-track { background: transparent; }
.shop-articles::-webkit-scrollbar-thumb { background: rgba(0,188,212,0.5); border-radius: 3px; }
.shop-articles::-webkit-scrollbar-thumb:hover { background: rgba(0,188,212,0.8); }
/* LIST */
.art-list { display: flex; flex-direction: column; gap: 1px; }
.art-list-row {
  display: flex;
  align-items: center;
  padding: 10px 20px 10px 12px;
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.04);
  border-radius: 6px;
  cursor: pointer;
  gap: 12px;
  transition: background 0.1s;
  user-select: none;
}
.art-list-row:hover { background: #F0FDFD; border-color: #00BCD4; }
.art-list-name { flex: 1; font-size: 14px; color: rgba(70,70,80,0.9); font-weight: 700; text-transform: capitalize; }
.art-list-code { font-size: 10px; color: #B0BEC5; }
.art-list-price { font-size: 12px; color: #00BCD4; font-weight: 700; white-space: nowrap; }
</style>
